import {
  idSchema,
  longitudeSchema,
  latitudeSchema,
  positionSchema,
  tidSchema,
} from '~/domains/shared/schema'
import * as z from 'zod'

const stateNames = [
  'Alabama',
  'Alaska',
  'Arizona',
  'Arkansas',
  'California',
  'Colorado',
  'Connecticut',
  'Delaware',
  'District of Columbia',
  'Florida',
  'Georgia',
  'Hawaii',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Kansas',
  'Kentucky',
  'Louisiana',
  'Maine',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'New York',
  'North Carolina',
  'North Dakota',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Pennsylvania',
  'Puerto Rico',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'Utah',
  'Vermont',
  'Virginia',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming',
] as const

const stateAbbrs = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DC',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'PR',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
] as const

const linearRingSchema = z.array(positionSchema).min(4)

const polygonGeometrySchema = z.object({
  coordinates: z.array(linearRingSchema).min(1),
  type: z.literal('Polygon'),
})

const multiPolygonGeometrySchema = z.object({
  coordinates: z.array(z.array(linearRingSchema).min(1)).min(1),
  type: z.literal('MultiPolygon'),
})

const stateFeaturePropertiesSchema = z.object({
  CENSUSAREA: z.number(),
  GEO_ID: z.string().regex(/^0400000US\d{2}$/),
  LSAD: z.literal(''),
  NAME: z.enum(stateNames),
  STATE: z.string().length(2).regex(/^\d+$/),
})

const stateFeatureSchema = z.object({
  geometry: z.discriminatedUnion('type', [
    polygonGeometrySchema,
    multiPolygonGeometrySchema,
  ]),
  properties: stateFeaturePropertiesSchema,
  type: z.literal('Feature'),
})

const submarketFeatureSchema = z.object({
  geometry: polygonGeometrySchema,
  type: z.literal('Feature'),
})

const geographyBaseSchema = z.object({
  _id: idSchema,
  stateAbbr: z.enum(stateAbbrs),
  tid: tidSchema,
})

const geographyStateSchema = z.object({
  ...geographyBaseSchema.shape,
  country: z.literal('USA'),
  feature: stateFeatureSchema,
  name: z.enum(stateNames),
  type: z.literal('state'),
})

const geographySubmarketSchema = z.object({
  ...geographyBaseSchema.shape,
  country: z.literal('US'),
  feature: submarketFeatureSchema,
  label: z.string().min(1).optional(),
  meta: z.object({
    'meta.submarket': z.string().min(1),
  }),
  name: z.string().min(1),
  stateAbbr: z.literal('NM'),
  type: z.literal('submarket'),
})

export const geographySchema = z.discriminatedUnion('type', [
  geographyStateSchema,
  geographySubmarketSchema,
])

export const geographyDtoSchema = geographySchema.transform((geography) => ({
  ...geography,
  _id: geography._id.toString(),
}))

export type Geography = z.output<typeof geographySchema>

export const mapLayerGeographyTypes = ['submarket'] as const satisfies readonly Geography["type"][]

export const mapLayerGeographiesInputSchema = z.object({
  types: z.array(z.enum(mapLayerGeographyTypes)).default([]),
})

export const geographyMapLayerDtoSchema = geographySubmarketSchema
  .pick({
    _id: true,
    feature: true,
    name: true,
    type: true,
  })
  .transform((geography) => ({
    ...geography,
    _id: geography._id.toString(),
  }))

export const geographySubmarketOptionDtoSchema = geographySubmarketSchema
  .pick({
    _id: true,
    label: true,
    name: true,
    type: true,
  })
  .transform((geography) => ({
    ...geography,
    _id: geography._id.toString(),
  }))

export const geographySubmarketDtoSchema = geographySubmarketSchema.transform(
  (geography) => ({
    ...geography,
    _id: geography._id.toString(),
  })
)

export const geographyContainingPointDtoSchema = geographySubmarketSchema
  .pick({
    _id: true,
    meta: true,
    type: true,
  })
  .transform((geography) => ({
    ...geography,
    _id: geography._id.toString(),
  }))

export const containingPointInputSchema = z.object({
  latitude: latitudeSchema,
  longitude: longitudeSchema,
})

export type GeographyDto = z.output<typeof geographyDtoSchema>
export type GeographyMapLayerDto = z.output<typeof geographyMapLayerDtoSchema>
export type GeographySubmarketDto = z.output<typeof geographySubmarketDtoSchema>
export type GeographySubmarketOptionDto = z.output<
  typeof geographySubmarketOptionDtoSchema
>
export type GeographyContainingPointDto = z.output<
  typeof geographyContainingPointDtoSchema
>
export type ContainingPointInput = z.input<typeof containingPointInputSchema>
export type MapLayerGeographyType = (typeof mapLayerGeographyTypes)[number]
export type MapLayerGeographiesInput = z.input<
  typeof mapLayerGeographiesInputSchema
>
